import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { Link, Redirect, useLocation } from "react-router-dom";

import { AuthLayout, LoginForm, useAuthErrorMessage } from "@mapmycustomers/auth";
import { LoadingSpinner } from "@mapmycustomers/ui";

import Path from "@app/enum/Path";
import { getError, isLoading, isSignedIn, usesLoginApp } from "@app/store/auth";
import {
  checkLoginAppRollout,
  resetErrors,
  signInThroughLoginApp,
  tryLogin,
} from "@app/store/auth/actions";
import { RootState } from "@app/store/rootReducer";
import analyticsService from "@app/util/analytic/AnalyticsService";

export const LoginPage = ({
  authenticate,
  error,
  isLoading,
  isSignedIn,
  onCheckLoginAppRollout,
  onResetError,
  onSignInThroughLoginApp,
  usesLoginApp,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const message = useAuthErrorMessage(error, {
    forgotPasswordUrl: Path.FORGOT_PASSWORD,
    LinkComponent: Link,
    onForgotPasswordClick: onResetError,
  });
  const { search } = useLocation();

  useEffect(() => {
    if (isSignedIn) {
      return;
    }
    if (usesLoginApp === undefined) {
      onCheckLoginAppRollout();
    } else if (usesLoginApp) {
      // The login app sends the browser back here with this flag when the API lost its sign-in. A
      // stored error counts too: this browser never renders the form that would show it.
      onSignInThroughLoginApp({
        failed: !!error || new URLSearchParams(search).get("mmc_sign_in_failed") === "1",
      });
    }
  }, [error, isSignedIn, onCheckLoginAppRollout, onSignInThroughLoginApp, search, usesLoginApp]);

  const handleForgotPasswordClick = useCallback(
    () => analyticsService.clicked(["Forgot password"]),
    []
  );
  const handleSsoLoginClick = useCallback(() => analyticsService.clicked(["SSO"]), []);

  // go to root to proceed when already signed in
  if (isSignedIn) {
    return <Redirect to={Path.ROOT} />;
  }

  if (usesLoginApp !== false) {
    return <LoadingSpinner global />;
  }

  return (
    <AuthLayout mapActions>
      <LoginForm
        errorMessage={message}
        forgotPasswordUrl={Path.FORGOT_PASSWORD}
        isLoading={isLoading}
        LinkComponent={Link}
        onForgotPasswordClick={handleForgotPasswordClick}
        onResetError={onResetError}
        onSsoLoginClick={handleSsoLoginClick}
        onSubmit={authenticate}
        signUpUrl={Path.REGISTER}
        ssoLoginUrl={Path.SSO_LOGIN}
      />
    </AuthLayout>
  );
};

const mapStateToProps = (state: RootState) => ({
  error: getError(state),
  isLoading: isLoading(state),
  isSignedIn: isSignedIn(state),
  usesLoginApp: usesLoginApp(state),
});

const mapDispatchToProps = {
  authenticate: tryLogin.request,
  onCheckLoginAppRollout: checkLoginAppRollout,
  onResetError: resetErrors,
  onSignInThroughLoginApp: signInThroughLoginApp,
};

export default connect(mapStateToProps, mapDispatchToProps)(LoginPage);
